import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Download, RefreshCw, Edit, Trash2, Building2, Users, CheckCircle, Building, Eye } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import type { Column } from '../../components/common/DataTable';
import StatCard from '../../components/common/StatCard';
import CompanyModal from '../../components/admin/CompanyModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { companyService } from '../../services';
import type { Company as ApiCompany } from '../../services';
import { useToast } from '../../components/common/ToastContainer';
import { usePermissions } from '../../hooks/usePermissions';

interface Company {
  id: string;
  name: string;
  domain: string;
  tenantCount: number;
  userCount: number;
  status: 'Active' | 'Suspended';
  createdAt: string;
}

const Companies = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [page] = useState(1);
  const [pageSize] = useState(20);

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | undefined>();
  const [deleteCompany, setDeleteCompany] = useState<Company | undefined>();
  const [isDeleting, setIsDeleting] = useState(false);

  // Map API company to UI format
  const mapApiCompanyToUi = (apiCompany: ApiCompany): Company => ({
    id: apiCompany.id,
    name: apiCompany.name,
    domain: apiCompany.email || '-',
    tenantCount: 0, // Not available in current API
    userCount: 0, // Not available in current API
    status: 'Active', // Not available in current API
    createdAt: apiCompany.createTime || '-',
  });

  // Fetch companies from API
  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await companyService.list({ page, pageSize });
      const uiCompanies = response.items.map(mapApiCompanyToUi);
      setCompanies(uiCompanies);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, [page, pageSize]);

  const handleRefresh = () => {
    fetchCompanies();
  };

  const handleSaveCompany = async (companyData: any) => {
    try {
      if (editingCompany) {
        // Update existing company
        await companyService.update(companyData.id, {
          name: companyData.name,
          address: companyData.address,
          email: companyData.email,
          phone: companyData.phone,
          description: companyData.description,
        });
      } else {
        // Create new company
        await companyService.create({
          name: companyData.name,
          address: companyData.address,
          email: companyData.email,
          phone: companyData.phone,
          description: companyData.description,
        });
      }
      // Refresh the list
      await fetchCompanies();
      setIsAddModalOpen(false);
      setEditingCompany(undefined);
    } catch (error) {
      console.error('Failed to save company:', error);
      toast.error('Save Failed', 'Failed to save company. Please try again.');
    }
  };

  const handleEditClick = (company: Company) => {
    setEditingCompany(company);
  };

  const handleDeleteClick = (company: Company) => {
    setDeleteCompany(company);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteCompany) return;

    try {
      setIsDeleting(true);
      await companyService.delete(deleteCompany.id);
      await fetchCompanies();
      setDeleteCompany(undefined);
    } catch (error) {
      console.error('Failed to delete company:', error);
      toast.error('Delete Failed', 'Failed to delete company. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: Column<Company>[] = [
    { key: 'name', label: 'Company Name', sortable: true, render: (c) => <div className="flex items-center space-x-2"><Building2 className="w-4 h-4 text-primary-600" /><span className="font-medium">{c.name}</span></div> },
    { key: 'domain', label: 'Domain', sortable: true },
    { key: 'tenantCount', label: 'Tenants', sortable: true },
    { key: 'userCount', label: 'Users', sortable: true, render: (c) => <div className="flex items-center space-x-1"><Users className="w-3 h-3 text-gray-400" /><span>{c.userCount}</span></div> },
    { key: 'status', label: 'Status', sortable: true, render: (c) => <span className={`px-2 py-1 text-xs font-medium rounded-full ${c.status === 'Active' ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-700'}`}>{c.status}</span> },
    { key: 'createdAt', label: 'Created', sortable: true },
    {
      key: 'actions',
      label: 'Actions',
      render: (c) => (
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => navigate(`/admin/companies/${c.id}`)}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            title="View Details"
          >
            <Eye className="w-4 h-4 text-gray-600 hover:text-primary-600" />
          </button>
          {canUpdate && (
          <button
            onClick={() => handleEditClick(c)}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            title="Edit Company"
          >
            <Edit className="w-4 h-4 text-gray-600 hover:text-primary-600" />
          </button>
          )}
          {canDelete && (
          <button
            onClick={() => handleDeleteClick(c)}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            title="Delete Company"
          >
            <Trash2 className="w-4 h-4 text-gray-600 hover:text-red-600" />
          </button>
          )}
        </div>
      )
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-bold text-gray-900">Companies</h1><p className="text-gray-600 mt-1">Manage company accounts</p></div>
        <div className="flex space-x-3"><button className="btn-secondary inline-flex items-center space-x-2"><Download className="w-4 h-4" /><span>Export</span></button><button onClick={handleRefresh} className="btn-secondary inline-flex items-center space-x-2"><RefreshCw className="w-4 h-4" /><span>Refresh</span></button>{canCreate && <button onClick={() => setIsAddModalOpen(true)} className="btn-primary inline-flex items-center space-x-2"><Plus className="w-4 h-4" /><span>Add Company</span></button>}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard icon={Building2} title="Total Companies" value={companies.length} color="primary" />
        <StatCard icon={CheckCircle} title="Active" value={companies.filter(c => c.status === 'Active').length} color="success" />
        <StatCard icon={Building} title="Total Tenants" value={companies.reduce((sum, c) => sum + c.tenantCount, 0)} color="info" />
        <StatCard icon={Users} title="Total Users" value={companies.reduce((sum, c) => sum + c.userCount, 0)} color="secondary" />
      </div>
      <div className="card"><DataTable columns={columns} data={companies} searchPlaceholder="Search companies..." loading={loading} /></div>

      {/* Add/Edit Company Modal */}
      <CompanyModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleSaveCompany}
        mode="add"
      />

      <CompanyModal
        isOpen={!!editingCompany}
        onClose={() => setEditingCompany(undefined)}
        onSave={handleSaveCompany}
        company={editingCompany ? {
          id: editingCompany.id,
          name: editingCompany.name,
          address: '',
          contact: '',
          phone: '',
          email: editingCompany.domain,
          description: '',
        } : undefined}
        mode="edit"
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteCompany}
        onClose={() => setDeleteCompany(undefined)}
        onConfirm={handleDeleteConfirm}
        title="Delete Company"
        message={`Are you sure you want to delete "${deleteCompany?.name}"?\n\nThis action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Companies;

